import { Injectable, InjectionToken, inject } from '@angular/core';
import * as api from '@opentelemetry/api';
import { Observable, first, map } from 'rxjs';

import { ResolvedServiceLocation } from '@mobi/rwc-utils-common-ng-jslib';

import { ApiDiscoveryIntegrationService } from './api-discovery.integration.service';
import { ApiDiscoveryEntry, Protocol } from './api-discovery.types';

type ServiceDefinition = Map<ApiDiscoveryEntry['path'], ApiDiscoveryEntry>;
export type ServiceDefinitionFinder = (
    relativePath: string,
    serviceDefinitions: ServiceDefinition
) => ApiDiscoveryEntry;

@Injectable({ providedIn: 'root' })
export class ServiceDiscoveryService {
    #serviceDefinitions = inject(ApiDiscoveryIntegrationService).apiDiscoveryResult$.pipe(
        first(),
        map(entries => new Map(entries.map(entry => [entry.path, entry])))
    );
    #getServiceDefinition = inject(SERVICE_DISCOVERY_FINDER);

    resolve(relativePath: string): Observable<ResolvedServiceLocation> {
        return this.#serviceDefinitions.pipe(
            map(serviceDefinitions => this.#getServiceDefinition(relativePath, serviceDefinitions)),
            map(serviceDefinition => ({
                path: this.#removeRestFromCloudPaths(relativePath, serviceDefinition),
                baseUrl: this.#getBaseUrl(serviceDefinition),
                versionHeader: this.#getVersionHeader(serviceDefinition)
            }))
        );
    }

    /**
     * OnPrem Services use /rest after their context path. They no longer use this in the cloud context
     * If a SPA receives the Cloud Endpoints for the current user but has still the OnPrem version of the call hardcoded
     * it will break. The service discovery tell us if we should remove the /rest for Cloud calls.
     */
    #removeRestFromCloudPaths(relativePath: string, serviceDefinition: ApiDiscoveryEntry) {
        if (!serviceDefinition.keepRestPathElement) {
            return relativePath.replace(`${serviceDefinition.path}/rest`, serviceDefinition.path);
        }
        return relativePath;
    }

    #getBaseUrl(serviceDefinition: ApiDiscoveryEntry): string {
        if (serviceDefinition.host) {
            const protocol = this.#selectProtocol(serviceDefinition);
            return `${protocol}://${serviceDefinition.host}`;
        } else {
            throw new Error(`Invalid service definition found: ${JSON.stringify(serviceDefinition)}`);
        }
    }

    #selectProtocol(serviceDefinition: ApiDiscoveryEntry) {
        if (serviceDefinition.protocols.includes(Protocol.HTTPS)) {
            return Protocol.HTTPS;
        } else if (serviceDefinition.protocols.includes(Protocol.HTTP)) {
            return Protocol.HTTP;
        }
        throw new Error(`No supported protocol found for service ${serviceDefinition.path}`);
    }

    #getVersionHeader(serviceDefinition: ApiDiscoveryEntry): ResolvedServiceLocation['versionHeader'] | undefined {
        const apiVersion = serviceDefinition.apiVersion;
        if (!apiVersion?.defaultVersion && apiVersion?.versionHeader && apiVersion?.name) {
            return {
                name: apiVersion.versionHeader,
                value: apiVersion.name
            };
        }
        return undefined;
    }
}

const serviceDefinitionFactory = (): ServiceDefinitionFinder => (relativePath, serviceDefinitions) => {
    const span = api.trace.getTracer('ServiceDiscovery').startSpan('ResolvedServiceLocation');
    span.setAttribute('api_entries', serviceDefinitions.size);
    const normalizedPath = normalizeServicePath(relativePath);
    let resolution: 'lookup' | 'fallback' | 'none' = 'none';
    try {
        const definition = serviceDefinitions.get(normalizedPath);
        if (definition) {
            resolution = 'lookup';
            return definition;
        }

        // Fallback matching
        const startTime = performance.now();
        const fallbackEntry = [...serviceDefinitions.values()].find(entry =>
            matchesServicePath(relativePath, entry.path)
        );
        span.addEvent(
            'Fallback matching',
            { found: !!fallbackEntry, duration: performance.now() - startTime },
            startTime
        );
        if (fallbackEntry) {
            resolution = 'fallback';
            return fallbackEntry;
        }

        throw new Error(
            `No service definition found for path ${relativePath} (normalized: ${normalizedPath}). Available service definitions: ${serviceDefinitions.size}.`
        );
    } finally {
        span.setAttribute('resolution', resolution);
        span.end();
    }
};

export const SERVICE_DISCOVERY_FINDER = new InjectionToken<ServiceDefinitionFinder>('SERVICE_DISCOVERY_FINDER', {
    factory: serviceDefinitionFactory
});

const NORMALIZE_PATH_REGEX = /^\/?([^/?]+\/[^/?]+(?:\/rest)?)/;

/**
 * Normalizes a relative path to its service discovery key.
 * Examples: /a/b/c/d -> a/b, a/b/c -> a/b, /a/b?c= -> a/b, a/b/rest/c -> a/b/rest
 */
function normalizeServicePath(relativePath: string): ApiDiscoveryEntry['path'] {
    return (relativePath.match(NORMALIZE_PATH_REGEX)?.[1] ?? relativePath) as ApiDiscoveryEntry['path'];
}

export function stripStartSlash(path: string): string {
    return path.replace(/^\/+/g, '');
}

export function matchesServicePath(requestPath: string, servicePath: string): boolean {
    return stripStartSlash(requestPath).startsWith(stripStartSlash(servicePath));
}
